/**
 * IndividualCipScanSection — DERIVED CIP section (Slot-4 / Contract C1).
 *
 * Bill 2026-05-27 Person-Modal deep-clean — converted from an inline-capture
 * surface to a DERIVED view:
 *   - The inline IdCapturePanel scan mount is GONE. Capture is now the single
 *     canonical top-of-review ReviewDropStrip (BL's capture component); this
 *     section no longer owns a scanner (North-Star §2.1 "kill inline capture").
 *   - id1 / id2 are rendered READ-ONLY from BL's `assignCipSlots` output
 *     (Contract C1) — it ranks captured photo-IDs by the admin CIP-ID priority
 *     catalog (`/v3/config/cip-id-priority`, C5) and never drops a captured ID
 *     (lower rank → id2). The manual ID fields are the EDIT affordance only.
 *   - person.extra CIP field shape is unchanged: id{Type,Number,Issuer,
 *     IssueDate,ExpiryDate} (ID1) + …2 (ID2). CD F-number labels preserved.
 *
 * Expiry behaviour: a past `idExpiryDate` forces the section open in Edit + a
 * red banner (re-capture required before CIF mod).
 */

import { useMemo } from 'react';
import type { RaoPerson } from '../../../types';
import { ID_TYPE_CODES } from './individual-config';
import type { AttachedDoc } from '../shared/attachedDocs';
import { DerivedSectionCard } from '../DerivedSectionCard';
import {
  assignCipSlots,
  type CapturedIdDoc,
  type CipIdPriorityRule,
  type CipSlotValue,
} from '../captureStub';

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers — idType, idNumber, etc. live on `person.extra`
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

function isDateInPast(iso: string): boolean {
  if (!iso) return false;
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return iso < `${yyyy}-${mm}-${dd}`;
}

function idTypeLabel(code: string): string {
  return ID_TYPE_CODES.find((t) => t.value === code)?.label ?? code;
}

/** Compact "Type · Number · Issuer" summary for the derived row. */
function slotSummary(slot: CipSlotValue | null): string {
  if (!slot) return '';
  return [idTypeLabel(slot.idType), slot.idNumber, slot.idIssuer].filter(Boolean).join(' · ');
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface IndividualCipScanSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  /**
   * @deprecated The inline scan panel is gone (capture is the top-of-review
   * ReviewDropStrip). Retained for prop-compat with existing callers; no-op.
   */
  onDocumentCaptured?: (doc: AttachedDoc) => void;
  /** Disables all inputs. */
  readOnly?: boolean;
  /**
   * @deprecated No scanner is mounted here any more. Retained for prop-compat.
   */
  cipScanEnabled?: boolean;
  /** CIP-ID priority catalog (`/v3/config/cip-id-priority`, C5). */
  cipIdPriority?: ReadonlyArray<CipIdPriorityRule>;
  /** D2 review-add ceremony — per-section Mark Reviewed Correct. */
  showMarkReviewed?: boolean;
  reviewed?: boolean;
  onToggleReviewed?: () => void;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function IndividualCipScanSection({
  person,
  onChange,
  mode,
  isChanged,
  readOnly = false,
  cipIdPriority = [],
  showMarkReviewed = false,
  reviewed = false,
  onToggleReviewed,
}: IndividualCipScanSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);

  const idType = readExtra(person, 'idType');
  const idNumber = readExtra(person, 'idNumber');
  const idIssuer = readExtra(person, 'idIssuer');
  const idIssueDate = readExtra(person, 'idIssueDate');
  const idExpiryDate = readExtra(person, 'idExpiryDate');

  // 2nd ID (optional). F-number drift RESOLVED 2026-06-01: canonical ID2 =
  // F814 (type) / 801 (number) / 813 (issuer) / 815 (issue) / 816 (expiry).
  // SPEC.md:1574 corrected (was F802-806 — F802 is the unrelated Tax-ID UDF).
  // Written at commit via the UDF path (single CustMod), NOT a 2nd IdVerifyAdd
  // (single-slot, proven). See cip-id-commit-and-slotmachine-build.md §1/§4.
  const idType2 = readExtra(person, 'idType2');
  const idNumber2 = readExtra(person, 'idNumber2');
  const idIssuer2 = readExtra(person, 'idIssuer2');
  const idIssueDate2 = readExtra(person, 'idIssueDate2');
  const idExpiryDate2 = readExtra(person, 'idExpiryDate2');

  const cip1HasData = Boolean(idType || idNumber);
  const secondIdHasData = Boolean(idType2 || idNumber2 || idIssuer2 || idIssueDate2 || idExpiryDate2);
  const isExpired = isDateInPast(idExpiryDate);

  // Contract C1 — render id1/id2 from assignCipSlots. Build the captured-ID
  // set from the current CIP fields (the canonical extracted/entered values),
  // then let the priority catalog decide which fills ID1 vs ID2. BL's
  // CapturedIdDoc keys provenance on `sourceDocCode` (not `docCode`) and derives
  // the slot's `idType` from `idTypeCode` — so the manual CIP `idType` code is
  // passed as `idTypeCode` for both ranking and the resolved slot value.
  const { id1, id2 } = useMemo(() => {
    const captures: CapturedIdDoc[] = [];
    if (cip1HasData) {
      captures.push({
        sourceDocCode: 'GOVT_ID',
        idTypeCode: idType,
        idNumber,
        idIssuer,
        idIssueDate,
        idExpiryDate,
      });
    }
    if (secondIdHasData) {
      captures.push({
        sourceDocCode: 'GOVT_ID_2',
        idTypeCode: idType2,
        idNumber: idNumber2,
        idIssuer: idIssuer2,
        idIssueDate: idIssueDate2,
        idExpiryDate: idExpiryDate2,
      });
    }
    return assignCipSlots(captures, cipIdPriority);
  }, [
    cip1HasData, secondIdHasData, idType, idNumber, idIssuer, idIssueDate, idExpiryDate,
    idType2, idNumber2, idIssuer2, idIssueDate2, idExpiryDate2, cipIdPriority,
  ]);

  const rows = [
    { label: 'Primary ID (F254-258)', value: slotSummary(id1) },
    { label: 'Primary expires (F258)', value: id1?.idExpiryDate ?? '', changed: isExpired },
    ...(id2 ? [{ label: '2nd ID (F814/801/813)', value: slotSummary(id2) }] : []),
  ];

  const banner = isExpired ? (
    <div
      role="alert"
      style={{
        padding: '10px 14px',
        background: 'var(--err-bg)',
        border: '1px solid var(--pcb-red, #b10505)',
        borderRadius: 6,
        fontSize: 13,
        color: 'var(--ink-900)',
      }}
    >
      <strong>ID expired — update required.</strong> The ID on file expired on{' '}
      <strong>{idExpiryDate}</strong>. Capture a current identification before committing the
      CIF modification.
    </div>
  ) : undefined;

  // The editable affordance — manual CIP fields (the ONLY entry path now that
  // the scanner is the top-of-review drop strip).
  const editBody = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-idtype">ID Type (Field 254)</label>
          <select
            id="rao-ind-idtype"
            value={idType}
            onChange={(e) => onChange(patchExtra(person, { idType: e.currentTarget.value }))}
            disabled={readOnly}
            style={changedFieldStyle(inputStyle, ic('idType'))}
            title="ID Type"
          >
            {ID_TYPE_CODES.map((t) => (
              <option key={t.value || 'none'} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-idnum">
            ID Number (Field 255)
          </label>
          <input
            id="rao-ind-idnum"
            value={idNumber}
            onChange={(e) => onChange(patchExtra(person, { idNumber: e.currentTarget.value }))}
            readOnly={readOnly}
            style={changedFieldStyle(inputStyle, ic('idNumber'))}
            title="ID Number — Core Director Field 255, the canonical CIP ID-number slot for the primary photo ID. (NOT F802: that is the unrelated 'Identification #1' UDF, which on real CIFs carries the entity Tax-ID / IRA plan number.)"
            maxLength={30}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-idissuer">ID Issuer (Field 256)</label>
          <input
            id="rao-ind-idissuer"
            value={idIssuer}
            onChange={(e) => onChange(patchExtra(person, { idIssuer: e.currentTarget.value }))}
            readOnly={readOnly}
            style={changedFieldStyle(inputStyle, ic('idIssuer'))}
            title="ID Issuer — state abbreviation, country, branch, or SSA"
            maxLength={20}
            placeholder="MO / US / USMC / SSA"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-idissue">Issue Date (Field 257)</label>
          <input
            id="rao-ind-idissue"
            type="date"
            value={idIssueDate}
            onChange={(e) => onChange(patchExtra(person, { idIssueDate: e.currentTarget.value }))}
            readOnly={readOnly}
            style={changedFieldStyle(inputStyle, ic('idIssueDate'))}
            title="Issue Date"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-ind-idexp">Expiry Date (Field 258)</label>
          <input
            id="rao-ind-idexp"
            type="date"
            value={idExpiryDate}
            onChange={(e) => onChange(patchExtra(person, { idExpiryDate: e.currentTarget.value }))}
            readOnly={readOnly}
            style={changedFieldStyle(
              isExpired ? { ...inputStyle, borderColor: 'var(--pcb-red, #b10505)' } : inputStyle,
              ic('idExpiryDate'),
            )}
            title="Expiry Date"
          />
        </div>
      </div>

      {/* Optional 2nd ID — F814/801/813/815/816 (drift resolved 2026-06-01;
          written at commit via the UDF path, see WriteSecondIdAsync). */}
      {!cip1HasData ? (
        <div
          style={{
            padding: '8px 12px',
            border: '1px dashed var(--ink-200)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--ink-500)',
            fontStyle: 'italic',
          }}
          title="Add a 2nd ID after filling in the primary CIP ID above"
        >
          Optional 2nd ID — available once CIP #1 is populated.
        </div>
      ) : (
        <details open={secondIdHasData} style={{ border: '1px solid var(--ink-100)', borderRadius: 6, padding: '8px 10px' }}>
          <summary style={{ fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue)', cursor: 'pointer' }}>
            Optional 2nd ID
            {/* B4 (Bill 2026-06-03): once a 2nd ID is captured/entered (e.g. the
                SS card → F814=5 / F801), badge it as the SECONDARY ID with its
                type so the designation is visible — it wasn't surfaced before. */}
            {secondIdHasData && (
              <span
                style={{
                  marginLeft: 8,
                  padding: '1px 8px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  background: 'var(--ink-100, #e5e7eb)',
                  color: 'var(--pcb-blue)',
                }}
              >
                Secondary ID
                {(() => {
                  const lbl = ID_TYPE_CODES.find((t) => t.value === idType2)?.label;
                  return lbl ? ` · ${lbl}` : '';
                })()}
              </span>
            )}
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle} htmlFor="rao-ind-idtype2">2nd ID Type (Field 814)</label>
                <select
                  id="rao-ind-idtype2"
                  value={idType2}
                  onChange={(e) => onChange(patchExtra(person, { idType2: e.currentTarget.value }))}
                  disabled={readOnly}
                  style={changedFieldStyle(inputStyle, ic('idType2'))}
                  title="2nd ID Type"
                >
                  {ID_TYPE_CODES.map((t) => (
                    <option key={t.value || 'none'} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle} htmlFor="rao-ind-idnum2">Identification #2 (Field 801)</label>
                <input
                  id="rao-ind-idnum2"
                  value={idNumber2}
                  onChange={(e) => onChange(patchExtra(person, { idNumber2: e.currentTarget.value }))}
                  readOnly={readOnly}
                  style={changedFieldStyle(inputStyle, ic('idNumber2'))}
                  title="2nd ID Number"
                  maxLength={30}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle} htmlFor="rao-ind-idissuer2">2nd ID Issuer (Field 813)</label>
                <input
                  id="rao-ind-idissuer2"
                  value={idIssuer2}
                  onChange={(e) => onChange(patchExtra(person, { idIssuer2: e.currentTarget.value }))}
                  readOnly={readOnly}
                  style={changedFieldStyle(inputStyle, ic('idIssuer2'))}
                  title="2nd ID Issuer — state abbreviation, country, branch, or SSA"
                  maxLength={20}
                  placeholder="MO / US / USMC / SSA"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="rao-ind-idissue2">2nd ID Issue Date (Field 815)</label>
                <input
                  id="rao-ind-idissue2"
                  type="date"
                  value={idIssueDate2}
                  onChange={(e) => onChange(patchExtra(person, { idIssueDate2: e.currentTarget.value }))}
                  readOnly={readOnly}
                  style={changedFieldStyle(inputStyle, ic('idIssueDate2'))}
                  title="2nd ID Issue Date"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="rao-ind-idexp2">2nd ID Exp Date (Field 816)</label>
                <input
                  id="rao-ind-idexp2"
                  type="date"
                  value={idExpiryDate2}
                  onChange={(e) => onChange(patchExtra(person, { idExpiryDate2: e.currentTarget.value }))}
                  readOnly={readOnly}
                  style={changedFieldStyle(
                    isDateInPast(idExpiryDate2)
                      ? { ...inputStyle, borderColor: 'var(--pcb-red, #b10505)' }
                      : inputStyle,
                    ic('idExpiryDate2'),
                  )}
                  title="2nd ID Expiry Date"
                />
              </div>
            </div>
          </div>
        </details>
      )}
    </div>
  );

  return (
    <DerivedSectionCard
      title="Customer Identification (CIP)"
      rows={rows}
      {...(banner ? { banner } : {})}
      readOnly={readOnly}
      showMarkReviewed={showMarkReviewed}
      reviewed={reviewed}
      {...(onToggleReviewed ? { onToggleReviewed } : {})}
      defaultEditing={mode === 'new' || isExpired || !cip1HasData}
    >
      {editBody}
    </DerivedSectionCard>
  );
}
